/**
 * Chart Color Utilities
 *
 * Theme-aware color helpers for chart components.
 * Uses ZUI Theme palette for consistent theming.
 */

/**
 * Get chart colors from ZUI Theme
 * @param {object} theme - ZUI theme object
 * @returns {object} Chart color configuration
 */
export const getChartColors = (theme) => ({
  // Primary palette for data series
  primary: theme.palette.primary.main,
  secondary: theme.palette.secondary.main,
  tertiary: theme.palette.tertiary.main,

  // Default series palette (for multi-series charts)
  // Uses ZUI Theme semantic colors for consistent theming
  series: [
    theme.palette.info.main, // Blue
    theme.palette.success.main, // Green
    theme.palette.warning.main, // Orange
    theme.palette.error.main, // Red
    theme.palette.tertiary.main, // Purple
    theme.palette.info.light, // Light blue/cyan
    theme.palette.success.light, // Light green
    theme.palette.warning.light // Light orange
  ],

  // Grid and axes
  grid: theme.palette.border.main || theme.palette.grey[200],
  axis: theme.palette.text.secondary || theme.palette.grey[600],

  // Tooltip
  tooltip: {
    background: theme.palette.fixed.grey[950] || theme.palette.grey[950],
    text: theme.palette.text.white || theme.palette.common.white,
    border: theme.palette.fixed.grey[900] || theme.palette.grey[900]
  },

  // Empty state
  empty: theme.palette.text.tertiary || theme.palette.grey[400]
});

/**
 * Get a color from the series palette by index
 * @param {number} index - Index of the color
 * @param {object} theme - ZUI theme object
 * @returns {string} Color hex value
 */
export const getSeriesColor = (index, theme) => {
  const colors = getChartColors(theme);
  return colors.series[index % colors.series.length];
};

/**
 * Get color from a color map or fall back to default
 * @param {string} key - Key to look up
 * @param {object} colorMap - Map of keys to colors
 * @param {string} fallback - Fallback color
 * @returns {string} Color hex value
 */
export const getColorFromMap = (key, colorMap, fallback) => colorMap?.[key] || fallback;
